import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface ApplicationError extends Error {
  info: string;
  status: number;
}

export const fetcher = async (url: string) => {
  const res = await fetch(url);

  if (!res.ok) {
    const error = new Error(
      "An error occurred while fetching the data."
    ) as ApplicationError;

    error.info = await res.json();
    error.status = res.status;

    throw error;
  }

  return res.json();
};

/**
 * Format tool names for display
 * Converts snake_case to Title Case and handles special cases
 */
export function formatToolName(toolName: string): string {
  // Special cases for tool names that need custom formatting
  const specialCases: Record<string, string> = {
    fal_generate_image: "Generate Image",
    fal_generate_video: "Generate Video",
    fal_edit_image: "Edit Image",
    fal_upload_file: "Upload File",
    fal_check_status: "Check Status",
    fal_get_result: "Get Result",
    web_search: "Web Search",
    web_scrape: "Web Scrape",
    get_youtube_video_info: "YouTube Video Info",
    get_youtube_video_subtitles: "YouTube Video Subtitles",
    get_youtube_channel_videos: "YouTube Channel Videos",
    get_youtube_channel_info: "YouTube Channel Info",
  };

  // Check if it's a special case
  if (specialCases[toolName]) {
    return specialCases[toolName];
  }

  // Default transformation: snake_case to Title Case
  return toolName
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Format a number with commas as thousands separators
 * @param value The number to format
 * @returns Formatted number string
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}

/**
 * Format a date in a human-readable format
 * @param date The date to format
 * @param options Formatting options
 * @returns Formatted date string
 */
export function formatDate(
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  }
): string {
  if (!date) return "N/A";

  const dateObj = date instanceof Date ? date : new Date(date);

  // Check if date is valid
  if (Number.isNaN(dateObj.getTime())) {
    return "Invalid date";
  }

  return new Intl.DateTimeFormat("en-US", options).format(dateObj);
}
